import { skipToken } from '@apollo/client/react'
import { type ComponentProps } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { Link } from 'react-router'
import { useAsync } from 'react-use'
import { match } from 'ts-pattern'
import type { JsonValue } from 'type-fest'
import { joinURL } from 'ufo'
import IconParkOutlineClose from '~icons/icon-park-outline/close'
import { UIDialog, UIDialogContent } from '~/components/ui/Modal'
import Text from '~/components/ui/Text'
import { mapDecorationTypeToProfilePageTab } from '~/components/UserProfile/constants'
import DecorationRenderer from '~/features/UserDecoration/components/UserDecoration'
import { DecorationType } from '~/services/apollo/generated/graphql'
import { useDecoration } from '~/services/apollo/user-decoration'
import { useMe } from '~/services/helpers/user'
import { cn } from '~/utils/cx'
import {
  isAppVersionGreaterThanOrEqualTo,
  navigateToNativePage,
} from '~/utils/jsBridge/implementation'
import { NativeNavigation } from '~/utils/jsBridge/nativeNavigation'
import { track } from '~/utils/tracker'
import css from './NewDecorationCongratDialog.module.css'

// TODO: Extract to a common hook
const useSupportNativeShopDecoration = () => {
  const { value: shopSupported } = useAsync(() =>
    isAppVersionGreaterThanOrEqualTo('3.27'),
  )

  const { value: myDecorationsSupported } = useAsync(() =>
    isAppVersionGreaterThanOrEqualTo('4.5.0'),
  )

  return {
    shopSupported,
    myDecorationsSupported,
  }
}

interface NewDecorationCongratDialogProps extends ComponentProps<
  typeof UIDialog
> {
  decorationCodes: string[]
  trackingArgs?: Record<string, JsonValue>
}

export default function NewDecorationCongratDialog({
  decorationCodes,
  trackingArgs,
  className,
  onClose,
  ...props
}: NewDecorationCongratDialogProps) {
  return (
    <UIDialog
      {...props}
      modalClassName={cn('relative', props.modalClassName)}
      className={cn(css.root, 'border-2 w-full max-w-screen-mmd', className)}
    >
      <NewDecorationCongratDialogContent
        decorationCodes={decorationCodes}
        trackingArgs={trackingArgs}
        onClose={onClose}
      />
    </UIDialog>
  )
}

NewDecorationCongratDialog.open = (
  decorationCodes: string[],
  opts?: Omit<
    NewDecorationCongratDialogProps,
    'open' | 'children' | 'decorationCodes'
  >,
) => {
  const { trackingArgs, className, ...dialogProps } = opts ?? {}

  if (decorationCodes.length > 0) {
    track('event_get_decoration', {
      decoration_type: decorationCodes.join(','),
      ...trackingArgs,
    })
  }

  UIDialog.create({
    dismissable: true,
    content: ({ close }) => (
      <NewDecorationCongratDialogContent
        decorationCodes={decorationCodes}
        trackingArgs={trackingArgs}
        onClose={close}
      />
    ),
    type: 'dialog',
    ...dialogProps,
    modalClassName: cn('relative', dialogProps.modalClassName),
    className: cn(css.root, 'border-2 w-full max-w-screen-mmd', className),
  })
}

const NewDecorationCongratDialogContent = (
  props: Pick<
    NewDecorationCongratDialogProps,
    'decorationCodes' | 'onClose' | 'trackingArgs'
  >,
) => {
  const { shopSupported, myDecorationsSupported } =
    useSupportNativeShopDecoration()
  const { me } = useMe()
  const firstDecorationCode = props.decorationCodes[0]
  const { data: firstDecoration } = useDecoration(
    firstDecorationCode ? { code: firstDecorationCode } : skipToken,
  )
  const firstDecorationType = firstDecoration?.type
  const urlSegment =
    firstDecorationType &&
    mapDecorationTypeToProfilePageTab(firstDecorationType)

  return (
    <>
      <AriaButton slot="close" className="p-2 absolute top-0 right-0">
        <IconParkOutlineClose className="size-5" />
      </AriaButton>
      <UIDialogContent className="msm:p-0 font-quicksand">
        <div className="relative w-full flex justify-center -top-3">
          <span
            data-part="title"
            className="uppercase px-4 py-1 rounded text-xs"
          >
            <Text i18nKey="common:messages.congratulations" />
          </span>
        </div>
        <div className="flex justify-center">
          <p
            data-part="heading"
            className="uppercase font-bold text-2xl my-4 text-center"
          >
            <Text i18nKey="growth:user-decoration.messages.user-unlocked-new-decoration" />
          </p>
        </div>
        <div className="flex flex-wrap gap-4 items-center justify-center mt-4 mb-8 mx-10">
          {props.decorationCodes.map(code => (
            <DecorationRenderer key={code} code={code}>
              {(data, Decoration) => (
                <Decoration
                  className={cn(
                    match(data)
                      .with(
                        { type: DecorationType.AvatarFrame },
                        () => 'size-28',
                      )
                      .with(
                        { type: DecorationType.ArtworkFrame },
                        () => 'size-28',
                      )
                      .with({ type: DecorationType.LiveBanner }, () => 'w-28')
                      .otherwise(() => 'size-32'),
                  )}
                />
              )}
            </DecorationRenderer>
          ))}
        </div>
        {urlSegment && (
          <div className="flex justify-center mb-4">
            <Link
              className="text-xs rounded-lg px-4 py-2 border-solid border-[#E8E8E8] border hover:bg-[#E8E8E8]/40"
              to={joinURL(`/@${me?.username}`, 'decorations', urlSegment)}
              onClick={ev => {
                track('event_navi_click', {
                  value: 'profile',
                  ...props.trackingArgs,
                })
                props.onClose?.()
                if (shopSupported || myDecorationsSupported) {
                  ev.preventDefault()
                  navigateToNativePage({
                    route: myDecorationsSupported
                      ? NativeNavigation.myDecorations()
                      : NativeNavigation.shop(),
                  })
                }
              }}
            >
              <Text i18nKey="growth:user-decoration.labels.view-in-profile" />
            </Link>
          </div>
        )}
      </UIDialogContent>
    </>
  )
}
